import type { FingeringPart, FingeringMode, FingeringRestoreRequest } from '../types/fingering';
import { loadPendingOriginalPdf } from './fingeringStorage';
import { loadJobLifecycleEntry } from './jobLifecycleStorage';

/**
 * 再投入の結果 (issue #116 FE-2)。
 * - `ok`: 復元可能。 抽出済み PDF + 元設定を restore で返す (呼び出し側がフォームへ載せる)。
 * - `expired`: 元 PDF の保持期限 (30分) 切れ / 欠損で復元不可 (再アップロード誘導)。
 */
export type RestoreOutcome =
  | { outcome: 'ok'; restore: FingeringRestoreRequest }
  | { outcome: 'expired' };

/**
 * 失敗ジョブを「メイン画面のフォームへ復元」するためのデータを組む (issue #116 FE-2)。
 *
 * #110 の「元 PDF + 元設定で即投入」を廃し、抽出済み (選択ページのみ) PDF + 元設定
 * (part/mode/scordatura/bpm/fileName) を読んで restoreRequest を返す。 ユーザーが設定を
 * 変えて自分で再投入できるようにするのが本置換の主眼。
 *
 * - 即投入 (submitFromPdf) はしない。
 * - 旧失敗 entry / PDF は温存する (復元は失敗を解消しないため、 履歴に残す。 spec §2)。
 * - #113 案C: page_range は撤去済み (PDF は既に選択ページのみ抽出済み)。
 */
export async function retryFailedJob(jobId: string): Promise<RestoreOutcome> {
  // 1. 抽出済み PDF を取得。 30分 TTL 切れ / 欠損なら復元不可 (再アップロード誘導)。
  const blob = await loadPendingOriginalPdf(jobId);
  if (blob === null) return { outcome: 'expired' };

  // 2. 元 entry から part/mode + 拡張 params (scordatura/bpm) を復元する。
  const params = loadJobLifecycleEntry(jobId)?.params;
  const restore: FingeringRestoreRequest = {
    file: blob,
    fileName: params?.fileName ?? '運指結果.pdf',
    part: (params?.part as FingeringPart) ?? 'Guitar1',
    mode: (params?.mode as FingeringMode) ?? 'part',
    scordatura: params?.scordatura,
    bpm: params?.bpm,
  };
  return { outcome: 'ok', restore };
}
